<template>
    <div>
        <v-card class="ma-2">
            <v-card-text>
                <v-row dense align="center">
                    <v-col cols="12" md="3">
                        <v-text-field
                            v-model="filtros.q"
                            label="Empresa, conexão ou key"
                            prepend-inner-icon="mdi-magnify"
                            outlined
                            dense
                            hide-details
                            clearable
                            @keyup.enter="carregar(1)"
                            @click:clear="carregar(1)"
                        />
                    </v-col>

                    <v-col cols="6" md="2">
                        <v-select
                            v-model="filtros.host"
                            :items="opcoesHost"
                            label="Host"
                            outlined
                            dense
                            hide-details
                            clearable
                            @change="onHost"
                        />
                    </v-col>

                    <v-col cols="6" md="2">
                        <v-select
                            v-model="filtros.porta"
                            :items="opcoesPorta"
                            label="Porta"
                            outlined
                            dense
                            hide-details
                            clearable
                            :disabled="!filtros.host"
                            @change="carregar(1)"
                        />
                    </v-col>

                    <v-col cols="6" md="2">
                        <v-select
                            v-model="filtros.situacao"
                            :items="opcoesSituacao"
                            label="Situação"
                            outlined
                            dense
                            hide-details
                            clearable
                            @change="carregar(1)"
                        />
                    </v-col>

                    <v-col cols="6" md="2">
                        <v-select
                            v-model="filtros.canal"
                            :items="[{ value: 'web', text: 'WhatsApp Web' }, { value: 'oficial', text: 'API Oficial' }]"
                            label="Canal"
                            outlined
                            dense
                            hide-details
                            clearable
                            @change="carregar(1)"
                        />
                    </v-col>

                    <v-col cols="12" md="1" class="text-right">
                        <v-btn icon :loading="loading" title="Reler o estado dos pods" @click="carregar(pagina, true)">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>

                <v-row v-if="podsSemResposta.length" dense>
                    <v-col cols="12">
                        <v-alert dense text type="warning" class="mb-0">
                            {{ podsSemResposta.length }} pod(s) não responderam
                            ({{ podsSemResposta.join(', ') }}). As instâncias deles aparecem como
                            "pod sem resposta" — não é o mesmo que estarem offline.
                        </v-alert>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-card class="ma-2">
            <v-toolbar flat dense>
                <v-toolbar-title class="subtitle-1">
                    {{ total }} instância(s)
                    <span v-if="selecionadas.length" class="grey--text">
                        · {{ selecionadas.length }} selecionada(s)
                    </span>
                </v-toolbar-title>

                <v-spacer/>

                <span v-if="lidoEm" class="caption grey--text mr-3">
                    estado lido {{ lidoHa }}
                </span>

                <v-btn
                    v-if="podeMover"
                    color="primary"
                    small
                    :disabled="!selecionadas.length"
                    @click="abrirMover"
                >
                    <v-icon left small>mdi-swap-horizontal</v-icon>
                    Mover selecionadas
                </v-btn>
            </v-toolbar>

            <v-data-table
                v-model="selecionadas"
                :headers="headers"
                :items="itens"
                :loading="loading"
                :mobileBreakpoint="0"
                :items-per-page="rows"
                :server-items-length="total"
                :page="pagina"
                :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
                item-key="id"
                show-select
                :single-select="false"
                dense
                @update:page="carregar($event)"
                @update:items-per-page="onRows"
            >
                <template v-slot:item.empresa_nome="{ item }">
                    <div>{{ item.empresa_nome || '—' }}</div>
                    <div class="caption grey--text">{{ item.empresa_dominio }}</div>
                </template>

                <template v-slot:item.name="{ item }">
                    <div>{{ item.name }}</div>
                    <div class="caption grey--text">{{ item.whatsapp || 'sem número' }}</div>
                </template>

                <template v-slot:item.endereco="{ item }">
                    <div>{{ nomeDoEndereco(item.endereco) }}</div>
                    <div class="caption grey--text">{{ item.endereco }}</div>
                    <div v-if="item.situacao === 'divergente'" class="caption deep-orange--text">
                        vivo em {{ item.carregada_em }}
                    </div>
                </template>

                <template v-slot:item.situacao="{ item }">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-chip x-small label dark :color="situacao(item.situacao).cor" v-on="on">
                                <v-icon x-small left>{{ situacao(item.situacao).icone }}</v-icon>
                                {{ situacao(item.situacao).texto }}
                            </v-chip>
                        </template>
                        <span>{{ situacao(item.situacao).ajuda }}</span>
                    </v-tooltip>

                    <v-chip v-if="item.campanhas" x-small label class="ml-1" color="purple" dark>
                        {{ item.campanhas }} campanha(s)
                    </v-chip>
                </template>

                <template v-slot:item.avisos="{ item }">
                    <v-tooltip v-if="item.sessao_efemera" bottom>
                        <template v-slot:activator="{ on }">
                            <v-icon small color="orange darken-2" v-on="on">mdi-alert-outline</v-icon>
                        </template>
                        <span>
                            Empresa sem assinatura: o restore descarta esta sessão no próximo
                            restart do container. Mover não resolve isso.
                        </span>
                    </v-tooltip>

                    <v-icon v-if="!item.active" small color="grey" title="Conexão inativa">mdi-pause-circle-outline</v-icon>
                    <v-icon v-if="item.default" small color="blue-grey" title="Conexão padrão">mdi-star</v-icon>
                </template>

                <template v-slot:item.acoes="{ item }">
                    <v-btn
                        v-if="podeMover && !item.cloud"
                        icon
                        small
                        title="Recarregar no mesmo pod"
                        @click="recarregar(item)"
                    >
                        <v-icon small>mdi-restart</v-icon>
                    </v-btn>

                    <v-btn
                        v-if="podeMover && !item.cloud"
                        icon
                        small
                        title="Mover só esta"
                        @click="abrirMover([item])"
                    >
                        <v-icon small>mdi-swap-horizontal</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
        </v-card>

        <MoverDialog ref="mover" @concluido="carregar(pagina, true)"/>
    </div>
</template>

<script>
import MoverDialog from './MoverDialog';
import { carregarPods, hosts, pods, nomeDoEndereco } from './pods';
import { situacao, OPCOES_SITUACAO } from './situacoes';

export default {
    name: 'TabInstancias',

    components: { MoverDialog },

    data: () => ({
        loading: false,
        itens: [],
        selecionadas: [],
        total: 0,
        pagina: 1,
        rows: 50,
        lidoEm: null,
        podsSemResposta: [],
        filtros: { q: '', host: null, porta: null, situacao: null, canal: null },
        opcoesSituacao: OPCOES_SITUACAO,
        headers: [
            { text: 'Empresa', value: 'empresa_nome' },
            { text: 'Conexão', value: 'name' },
            { text: 'Pod', value: 'endereco' },
            { text: 'Situação', value: 'situacao', sortable: false },
            { text: '', value: 'avisos', sortable: false, width: 90 },
            { text: '', value: 'acoes', sortable: false, width: 90 },
        ],
    }),

    async mounted() {
        try {
            await carregarPods(this.$http);
        } catch (e) {
            this.notify('Não foi possível carregar o registro de pods', 'error');
        }

        this.carregar(1);
    },

    computed: {
        podeMover() {
            return this.checkPermission('zapercontrol_instancias_mover');
        },

        opcoesHost() {
            return hosts();
        },

        opcoesPorta() {
            return pods()
                .filter(p => p.host === this.filtros.host)
                .map(p => ({ value: String(p.porta), text: String(p.porta) }));
        },

        lidoHa() {
            return this.lidoEm ? this.moment(this.lidoEm).fromNow() : '';
        },
    },

    methods: {
        situacao,
        nomeDoEndereco,

        onHost() {
            this.filtros.porta = null;
            this.carregar(1);
        },

        onRows(rows) {
            this.rows = rows;
            this.carregar(1);
        },

        async carregar(pagina = 1, atualizar = false) {
            this.loading = true;
            this.pagina = pagina;

            const params = {
                page: pagina,
                rows: this.rows,
                q: this.filtros.q || undefined,
                host: this.filtros.host || undefined,
                porta: this.filtros.porta || undefined,
                situacao: this.filtros.situacao || undefined,
                canal: this.filtros.canal || undefined,
                atualizar: atualizar ? 1 : undefined,
            };

            try {
                const { data } = await this.$http.get('instancias-whatsapp', { params });

                this.itens = data.data || [];
                this.total = (data.meta && data.meta.total) || 0;
                this.podsSemResposta = (data.meta && data.meta.pods_sem_resposta) || [];
                this.lidoEm = new Date();
                this.selecionadas = [];
            } catch (e) {
                this.notify('Não foi possível carregar as instâncias', 'error');
            }

            this.loading = false;
        },

        abrirMover(itens) {
            const alvo = Array.isArray(itens) ? itens : this.selecionadas;

            const moviveis = alvo.filter(i => !i.cloud);

            if (!moviveis.length) {
                this.notify('Conexões da API Oficial não rodam em pod — não há o que mover', 'warning');
                return;
            }

            this.$refs.mover.abrir(moviveis);
        },

        async recarregar(item) {
            const ok = await this.$root.$confirm(
                'Recarregar instância',
                `A sessão de ${item.empresa_nome || item.name} será derrubada e recriada em `
                + `${item.endereco}. Leva alguns segundos e não muda o pod.`
            );

            if (!ok) return;

            try {
                const { data } = await this.$http.post(`instancias-whatsapp/${item.id}/recarregar`);

                this.notify(data.msg || 'Instância recarregada', data.type || 'success');
                setTimeout(() => this.carregar(this.pagina, true), 3000);
            } catch (e) {
                this.notify('Não foi possível recarregar', 'error');
            }
        },
    },
};
</script>
